import { useRouter } from 'next/router';
import { Box } from '@zoopla-eng/zds-components/box';
import { Grid } from '@zoopla-eng/zds-components/grid';
import { useIsLoggedIn } from '@zoopla/user';
import { ZooplaLogo } from '@zoopla/assets';
import {
    SaveMedium,
    UserMedium,
    HomeMedium
} from '@zoopla-eng/zds-components/icons';
import {
    ActionListItem,
    Container,
    MenuItem,
    Nav,
    LinkList,
    ActionList,
    SkipToContent
} from './Header.styles';
import { UpdateIndicator } from '@zoopla/notifications';
import { Button } from '@zoopla-eng/zds-components/button';
import { Link, LinkWithIcon } from '@zoopla-eng/zds-components/link';
import Menu from './Menu';
import { getSigninUrl, Navigation, trackAccount, trackNav } from './data';
import {
    useUnseenNotificationCount,
    largeScreenNotificationsFeature,
    mojoNotificationFlag
} from '@zoopla/notifications';
import { useIsHomeOwner } from './graphql/useIsHomeOwner';
import { useLazyOptimizelyFeatureFlag } from '@zoopla/optimizely';

export default function Header() {
    const { asPath } = useRouter();
    const isLoggedIn = useIsLoggedIn();
    const signinUrl = getSigninUrl(asPath);

    const [, largeScreenNotificationsEnabled] = useLazyOptimizelyFeatureFlag(
        largeScreenNotificationsFeature
    );

    const [isLoadingMojoNotificationFlag, showMojoNotification] =
        useLazyOptimizelyFeatureFlag(mojoNotificationFlag);

    const displayNotificationsIcon = useIsHomeOwner(!isLoggedIn);

    const [unseenNotificationsCount, setLastSeenDate] =
        useUnseenNotificationCount(
            !(displayNotificationsIcon && !isLoadingMojoNotificationFlag),
            showMojoNotification
        );

    const hasUnseenNotifications =
        unseenNotificationsCount && unseenNotificationsCount > 0;

    return (
        <Container>
            <SkipToContent>
                <Button
                    {...{ as: 'a', href: '#main-content' }}
                    category="primary"
                >
                    Skip to main content
                </Button>
            </SkipToContent>
            <Nav
                aria-label="main"
                data-testid={`header`}
                data-is-logged-in={isLoggedIn ? 'true' : undefined}
            >
                <Grid.Item area="headermiddle" bpL100={{ area: 'headerleft' }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bpL100={{ justifyContent: 'left' }}
                    >
                        <Box width="110px" height="20px">
                            <a
                                href="/"
                                aria-label="Zoopla's Homepage"
                                data-testid="zoopla-logo"
                            >
                                <ZooplaLogo />
                            </a>
                        </Box>
                    </Box>
                </Grid.Item>
                <Grid.Item area="headerleft" bpL100={{ area: 'headermiddle' }}>
                    <Box bpL100={{ display: 'none' }}>
                        <Menu isLoggedIn={isLoggedIn} signinUrl={signinUrl} />
                    </Box>
                    <Box
                        display="none"
                        bpL100={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <LinkList>
                            {Navigation.map(({ title, href }, idx) => (
                                <li
                                    key={href}
                                    data-testid={`header-menu${idx}`}
                                >
                                    <Link
                                        href={href}
                                        onClick={trackNav(title)}
                                        muted
                                        disableVisited
                                    >
                                        {title}
                                    </Link>
                                </li>
                            ))}
                        </LinkList>
                    </Box>
                </Grid.Item>
                <Grid.Item area="headerright">
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        bpL100={{ display: 'none' }}
                    >
                        <ActionList>
                            <>
                                <ActionListItem>
                                    <LinkWithIcon
                                        onClick={() => {
                                            trackAccount('my home')();
                                            setLastSeenDate();
                                        }}
                                        href="/my-home/"
                                        iconPrefix={
                                            <HomeMedium title="My Home" />
                                        }
                                        stacked
                                        muted
                                        disableVisited
                                        data-testid="my-home-mobile-link"
                                    >
                                        My Home
                                        {hasUnseenNotifications ? (
                                            <UpdateIndicator
                                                data-testid="header-notifications-new"
                                                count={
                                                    hasUnseenNotifications
                                                        ? String(
                                                              unseenNotificationsCount
                                                          )
                                                        : undefined
                                                }
                                                type={
                                                    hasUnseenNotifications
                                                        ? 'navVariant'
                                                        : undefined
                                                }
                                            />
                                        ) : null}
                                    </LinkWithIcon>
                                </ActionListItem>
                                <ActionListItem>
                                    <LinkWithIcon
                                        onClick={trackAccount(
                                            'favourite properties'
                                        )}
                                        href="/myaccount/favourites/"
                                        iconPrefix={
                                            <SaveMedium title="Saved" />
                                        }
                                        stacked
                                        muted
                                        disableVisited
                                    >
                                        Saved
                                    </LinkWithIcon>
                                </ActionListItem>
                                {!isLoggedIn && (
                                    <ActionListItem>
                                        <LinkWithIcon
                                            onClick={trackAccount('sign in')}
                                            href={signinUrl}
                                            iconPrefix={
                                                <UserMedium title="Sign in" />
                                            }
                                            stacked
                                            muted
                                            disableVisited
                                        >
                                            Sign in
                                        </LinkWithIcon>
                                    </ActionListItem>
                                )}
                            </>
                        </ActionList>
                    </Box>
                    <Box
                        display="none"
                        justifyContent="flex-end"
                        bpL100={{ display: 'flex' }}
                    >
                        <LinkList>
                            <MenuItem>
                                <Link
                                    href="/my-home/"
                                    onClick={() => {
                                        trackAccount('my home')();
                                        if (largeScreenNotificationsEnabled) {
                                            setLastSeenDate();
                                        }
                                    }}
                                    muted
                                    disableVisited
                                    data-testid="my-home-desktop-link"
                                >
                                    My Home
                                    {largeScreenNotificationsEnabled &&
                                    hasUnseenNotifications ? (
                                        <UpdateIndicator
                                            data-testid="header-notifications-new"
                                            count={
                                                hasUnseenNotifications
                                                    ? String(
                                                          unseenNotificationsCount
                                                      )
                                                    : undefined
                                            }
                                            type={
                                                hasUnseenNotifications
                                                    ? 'navVariant'
                                                    : undefined
                                            }
                                        />
                                    ) : null}
                                </Link>
                            </MenuItem>
                            <MenuItem>
                                <Link
                                    href="/myaccount/favourites/"
                                    onClick={trackAccount(
                                        'favourite properties'
                                    )}
                                    muted
                                    disableVisited
                                >
                                    Saved
                                </Link>
                            </MenuItem>
                            {isLoggedIn ? (
                                <MenuItem>
                                    <Link
                                        href="/my-zoopla/"
                                        onClick={trackAccount()}
                                        muted
                                        disableVisited
                                    >
                                        Account
                                    </Link>
                                </MenuItem>
                            ) : (
                                <MenuItem>
                                    <Link
                                        href={signinUrl}
                                        onClick={trackAccount('sign in')}
                                        muted
                                        disableVisited
                                    >
                                        Sign in
                                    </Link>
                                </MenuItem>
                            )}
                        </LinkList>
                    </Box>
                </Grid.Item>
            </Nav>
        </Container>
    );
}
